import React from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';
import type { ModelLineage } from '../../../types';

interface Props {
  data: ModelLineage;
}

function LineageNode({
  label,
  title,
  subtitle,
  color = '#1976d2',
}: {
  label: string;
  title: string;
  subtitle?: string;
  color?: string;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        px: 2,
        py: 1.5,
        borderRadius: 2,
        border: `2px solid ${color}`,
        backgroundColor: `${color}10`,
        minWidth: 140,
        textAlign: 'center',
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="subtitle2" fontWeight={700}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

function Arrow() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 0.5,
        color: '#9e9e9e',
        fontSize: 20,
      }}
    >
      →
    </Box>
  );
}

export default function LineageGraph({ data }: Props) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Model Lineage
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          overflowX: 'auto',
          gap: 1,
          py: 1,
        }}
      >
        {data.dataset && (
          <>
            <LineageNode
              label="Dataset"
              title={data.dataset.name}
              subtitle={data.dataset.source_type}
              color="#4caf50"
            />
            <Arrow />
          </>
        )}
        {data.dataset_version && (
          <>
            <LineageNode
              label="Dataset Version"
              title={`v${data.dataset_version.version_number}`}
              subtitle={`${data.dataset_version.record_count} rows`}
              color="#66bb6a"
            />
            <Arrow />
          </>
        )}
        {data.feature_set && (
          <>
            <LineageNode
              label="Feature Set"
              title={data.feature_set.name}
              color="#ff9800"
            />
            <Arrow />
          </>
        )}
        {data.training_job && (
          <>
            <LineageNode
              label="Training Job"
              title={`#${data.training_job.id}`}
              subtitle={data.training_job.algorithm || data.training_job.status}
              color="#f44336"
            />
            <Arrow />
          </>
        )}
        <LineageNode
          label="Model Asset"
          title={data.model_asset.name}
          subtitle={`${data.model_asset.family} / ${data.model_asset.framework}`}
          color="#9c27b0"
        />
        <Arrow />
        <LineageNode
          label="Model Version"
          title={`v${data.model_version.version_number}`}
          subtitle={data.model_version.stage}
          color="#1976d2"
        />
      </Box>
    </Paper>
  );
}
